/**
 * @file AuthLayout.tsx
 * @brief Split-screen shell for unauthenticated pages (login, forgot password).
 *
 * @details
 * Desktop mirrors the Facebook login composition: left visual/brand panel,
 * faint vertical divider, right interaction column. Mobile collapses to the
 * form column with a compact brand header. Theme toggle sits top-right of
 * the interaction panel (Facebook's gear position).
 *
 * @author DES
 * @date 26-05-2026
 * @updated 27-07-2026 — Facebook-style split layout
 */

import { Moon, Sun } from "lucide-react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BrandIcon } from "@/components/brand/BrandIcon";
import { BrandWordmark } from "@/components/brand/BrandWordmark";
import { Button } from "@/components/ui/Button";
import { env } from "@/shared/config/env";
import { cn } from "@/shared/theme/cn";
import { useTheme } from "@/shared/theme/theme-context";
import { AuthHeroCollage } from "./components/AuthHeroCollage";
import { AuthQuestionMarquee } from "./components/AuthQuestionMarquee";
import { AuthPromiseScroller } from "./components/AuthPromiseScroller";
import { AuthHeadline } from "./components/AuthHeadline";

/**
 * TEMP (DES 01-10-2026: "desktop per say mobile image hide kardo,
 * temporaray"): the phone mockup is hidden so the language network has the
 * panel. `true` brings it back.
 */
const SHOW_PHONE_MOCKUP = false;

export function AuthLayout() {
  const { resolvedTheme, setTheme } = useTheme();
  // Sign-up forms get a wide column so they fit one screen without
  // scrolling, the desktop app's window included (DES 01-10-2026: "chooti
  // screen per form khulnay per page scroll na karna paray").
  const wide = useLocation().pathname.startsWith("/register");

  return (
    // The app's page colour, not white (DES 01-10-2026: "nahin yaar match karo").
    <div className="flex min-h-screen flex-col bg-app-page text-foreground">
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* ------------------------------------------------------------------
            Left — brand + collage + headline (desktop only)
            ------------------------------------------------------------------ */}
        <aside
          className={cn(
            "relative isolate hidden flex-col justify-between overflow-hidden px-12 pb-12 pt-8 lg:flex",
            wide ? "lg:w-[40%]" : "lg:w-[68%]",
          )}
        >
          {/* The two red radial glows that sat here are gone (DES 01-10-2026:
              "remove the red flare or splash"); the network is the backdrop. */}
          {/* The canvas "hello" network that sat here ran a 60 fps
              JavaScript loop for as long as the login was open (DES
              01-10-2026: "its too heavy"). Replaced by the website's question
              marquee above the headline: CSS transforms only, and it pauses
              after a minute with no input. The phone keeps its own network. */}

          <BrandIcon size={40} />

          <div className="flex min-h-0 flex-1 items-center justify-center py-4 pl-10 xl:pl-16">
            {SHOW_PHONE_MOCKUP ? <AuthHeroCollage /> : null}
          </div>

          {/* Full panel width (cancels the aside's px-12), above the headline
              (DES: "speak every language kay ooper"). */}
          <AuthQuestionMarquee className="-mx-12 mb-8" />

          <div className="mt-2 max-w-md">
            {/* DES 03-10-2026: "speak every language aik ghalat message hai"
                (we support a set list, not every language). Then: "Speak
                <their language>. / Be heard in theirs.", the language from
                the visitor's region; "One table. / Many tongues." when there
                is no region to go on. */}
            <AuthHeadline />
            <AuthPromiseScroller />
          </div>
        </aside>

        {/* Subtle vertical divider with running flares */}
        <div aria-hidden className="auth-divider hidden lg:block">
          <span className="auth-divider__flare" />
        </div>

        {/* ------------------------------------------------------------------
            Right — form column
            ------------------------------------------------------------------ */}
        <main
          className={cn(
            "relative flex w-full flex-1 flex-col lg:flex-none",
            wide ? "lg:w-[60%]" : "lg:w-[32%]",
          )}
        >
          <div className="absolute right-4 top-4 z-10 sm:right-6 sm:top-6">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Toggle theme"
              onClick={() =>
                setTheme(resolvedTheme === "dark" ? "light" : "dark")
              }
            >
              {resolvedTheme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div
            className={cn(
              "flex flex-1 flex-col items-center justify-center px-6 sm:px-10",
              wide ? "py-8 [@media(max-height:720px)]:py-4" : "py-16",
            )}
          >
            {/* Mobile brand — desktop shows mark on the left panel instead */}
            <div
              className={cn(
                "flex flex-col items-center text-center lg:hidden",
                wide ? "mb-6 [@media(max-height:720px)]:hidden" : "mb-10",
              )}
            >
              <BrandWordmark height={32} className="mb-2" />
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Control Panel · {env.appEnv}
              </p>
            </div>

            <div className={cn("w-full", wide ? "max-w-[680px]" : "max-w-[360px]")}>
              <Outlet />

              {/* Wide (sign-up): no wordmark under the form; the brand is in the
                  left panel or the top header, and the form must fit one screen. */}
              <div className={cn("flex flex-col items-center gap-2", wide ? "hidden" : "mt-10")}>
                <BrandWordmark height={22} />
                <p className="hidden text-[11px] uppercase tracking-[0.18em] text-muted-foreground lg:block">
                  Control Panel · {env.appEnv}
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>

      <footer className="relative overflow-visible px-6 py-4 text-center text-[11px] text-muted-foreground">
        <div aria-hidden className="auth-footer-divider">
          <span className="auth-footer-divider__flare auth-footer-divider__flare--right" />
          <span className="auth-footer-divider__flare auth-footer-divider__flare--left" />
        </div>
        &copy; {new Date().getFullYear()} Verblizr. All rights reserved.
        {/* Google Play / Apple (B7, 22-10-2026): deletion without signing in. */}
        <span aria-hidden> · </span>
        <Link to="/delete-account" className="underline-offset-2 hover:text-foreground hover:underline">
          Delete your account
        </Link>
      </footer>
    </div>
  );
}
